import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaUsers, FaThumbsUp, FaStar, FaSearch, FaFilter, FaSave, FaEye, FaTimes, FaHandshake } from "react-icons/fa";
import api from "../../services/axios";
import TokenInvalido from "./tokenInvalido";
import "./selecaoParticipantes.css";

const SelecaoParticipantes = () => {
  const { token } = useParams();
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenValido, setTokenValido] = useState(null);
  const [patrocinador, setPatrocinador] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEmpresa, setFilterEmpresa] = useState("");
  const [filterCargo, setFilterCargo] = useState("");
  
  // Estados para os grupos de seleção
  const [maisQueroConversar, setMaisQueroConversar] = useState([]);
  const [menosQueroConversar, setMenosQueroConversar] = useState([]);
  
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Função para normalizar caracteres acentuados
  const normalizarTexto = (texto) => {
    return texto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Notificações
  const notifySuccess = (mensagem) => {
    console.log("Toast Success:", mensagem);
    toast.success(mensagem);
  };
  
  const notifyError = (mensagem) => {
    console.log("Toast Error:", mensagem);
    toast.error(mensagem);
  };
  
  const notifyInfo = (mensagem) => {
    console.log("Toast Info:", mensagem);
    toast.info(mensagem);
  };

  // Validar token e carregar participantes
  useEffect(() => {
    const validarTokenECarregarParticipantes = async () => {
      if (!token) {
        setTokenValido(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // Validar token
        const responseValidacao = await api.post("/patrocinador-selecao-valida-token", {
          token: token
        });
        if (responseValidacao.status === 200 && responseValidacao.data.patrocinador) {
          setTokenValido(true);
          setPatrocinador(responseValidacao.data.patrocinador);
          setParticipantes(responseValidacao.data.participantes || []);
          
          // Carregar seleções já feitas anteriormente
          if (responseValidacao.data.participantesMaisQuero) {
            setMaisQueroConversar(responseValidacao.data.participantesMaisQuero);
          }
          
          if (responseValidacao.data.participantesQuero) {
            setMenosQueroConversar(responseValidacao.data.participantesQuero);
          }
        } else {
          setTokenValido(false);
        }
      } catch (error) {
        console.error("Erro ao validar token ou carregar participantes:", error);
        setTokenValido(false);
      } finally {
        setLoading(false);
      }
    };

    validarTokenECarregarParticipantes();
  }, [token]);

  // Filtros aplicados
  const participantesFiltrados = useMemo(() => {
    return participantes.filter(participante => {
      const matchNome = normalizarTexto(participante.nome).includes(normalizarTexto(searchTerm));
      const matchEmpresa = filterEmpresa === "" || normalizarTexto(participante.empresa).includes(normalizarTexto(filterEmpresa));
      const matchCargo = filterCargo === "" || normalizarTexto(participante.cargo).includes(normalizarTexto(filterCargo));
      
      return matchNome && matchEmpresa && matchCargo;
    });
  }, [participantes, searchTerm, filterEmpresa, filterCargo]);

  // Participantes disponíveis (que não estão em nenhum grupo)
  const participantesDisponiveis = useMemo(() => {
    const idsJaSelecionados = [
      ...maisQueroConversar.map(p => p.id),
      ...menosQueroConversar.map(p => p.id)
    ];
    
    return participantesFiltrados.filter(p => !idsJaSelecionados.includes(p.id));
  }, [participantesFiltrados, maisQueroConversar, menosQueroConversar]);

  // Opções únicas para filtros
  const empresasUnicas = useMemo(() => {
    return [...new Set(participantes.map(p => p.empresa))].sort();
  }, [participantes]);

  const cargosUnicos = useMemo(() => {
    return [...new Set(participantes.map(p => p.cargo))].sort();
  }, [participantes]);

  // Limites máximos baseados no plano do patrocinador
  const limiteMaisQuero = patrocinador?.plano?.escolha_maxima_mais_quero || 5;
  const limiteQuero = patrocinador?.plano?.escolha_maxima_quero || 5;

  // Função para adicionar participante a um grupo
  const adicionarAoGrupo = (participante, grupo) => {
    if (grupo === 'mais' && maisQueroConversar.length >= limiteMaisQuero) {
      notifyError(`Você só pode selecionar até ${limiteMaisQuero} participantes para 'Agendamento Prioritário'`);
      return;
    }
    
    if (grupo === 'menos' && menosQueroConversar.length >= limiteQuero) {
      notifyError(`Você só pode selecionar até ${limiteQuero} participantes para 'Agendamento Desejável'`);
      return;
    }

    switch (grupo) {
      case 'mais':
        setMaisQueroConversar(prev => [...prev, participante]);
        break;
      case 'menos':
        setMenosQueroConversar(prev => [...prev, participante]);
        break;
    }
  };

  // Função para remover participante de um grupo
  const removerDoGrupo = (participanteId, grupo) => {
    switch (grupo) {
      case 'mais':
        setMaisQueroConversar(prev => prev.filter(p => p.id !== participanteId));
        break;
      case 'menos':
        setMenosQueroConversar(prev => prev.filter(p => p.id !== participanteId));
        break;
    }
  };

  const totalSelecionados = maisQueroConversar.length + menosQueroConversar.length;

  // Função para salvar seleções (parcial ou completa; pode repetir enquanto o token for válido)
  const salvarSelecoes = async () => {
    setSaving(true);
    try {
      const payload = {
        token: token,
        mais_quero_conversar: maisQueroConversar.map(p => p.id),
        menos_quero_conversar: menosQueroConversar.map(p => p.id)
      };
      
      const response = await api.post("/participantes-selecao-salvar", payload);
      
      if (response.status === 200 || response.status === 201) {
        notifySuccess(
          response.data?.mensagem ||
            "Seleções salvas com sucesso! Você pode continuar editando e salvar novamente."
        );
      } else {
        notifyError("Erro ao salvar seleções");
      }
    } catch (error) {
      console.error("Erro ao salvar seleções:", error);
      const mensagemApi = error.response?.data?.mensagem;
      notifyError(mensagemApi || "Erro ao salvar seleções");
    } finally {
      setSaving(false);
    }
  };

  // Limpar filtros
  const limparFiltros = () => {
    setSearchTerm("");
    setFilterEmpresa("");
    setFilterCargo("");
  };

  // Verificar se o token é inválido
  if (!loading && tokenValido === false) {
    return <TokenInvalido />;
  }

  if (loading) {
    return (
      <div className="selecao-participantes-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Validando acesso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="selecao-participantes-container">
      <div className="header-selecao">
        <div className="titulo-secao">
          <FaUsers className="icone-titulo" />
          <h2>Seleção de Participantes</h2>
        </div>
        <p className="subtitulo-selecao">
          Organize os participantes em grupos conforme sua preferência de networking
        </p>
      </div>

      {/* Filtros de Busca */}
      <div className="filtros-container">
        <div className="filtros-busca">
          <div className="input-busca">
            <FaSearch className="icone-busca" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-filtro"
            />
          </div>
          
          <select
            value={filterEmpresa}
            onChange={(e) => setFilterEmpresa(e.target.value)}
            className="select-filtro"
          >
            <option value="">Todas as empresas</option>
            {empresasUnicas.map(empresa => (
              <option key={empresa} value={empresa}>{empresa}</option>
            ))}
          </select>

          <select
            value={filterCargo}
            onChange={(e) => setFilterCargo(e.target.value)}
            className="select-filtro"
          >
            <option value="">Todos os cargos</option>
            {cargosUnicos.map(cargo => (
              <option key={cargo} value={cargo}>{cargo}</option>
            ))}
          </select>

          <button onClick={limparFiltros} className="btn-limpar-filtros">
            <FaFilter /> Limpar
          </button>
        </div>
      </div>

      <div className="conteudo-selecao">
        {/* Lista de Participantes Disponíveis */}
        <div className="secao-participantes">
          <h3 className="titulo-secao-participantes">
            Participantes Disponíveis ({participantesDisponiveis.length})
          </h3>
          
          {participantesDisponiveis.length === 0 ? (
            <div className="lista-vazia">
              <p>Nenhum participante encontrado com os filtros aplicados</p>
            </div>
          ) : (
            <div className="lista-participantes">
              {participantesDisponiveis.map(participante => (
                <div
                  key={participante.id}
                  className={`card-participante${participante.interesse_empresa ? " tem-interesse" : ""}`}
                >
                  <div className="info-participante">
                    <h4 className="nome-participante">{participante.nome}</h4>
                    <p className="empresa-participante">{participante.empresa}</p>
                    <p className="cargo-participante">{participante.cargo}</p>
                    {participante.interesse_empresa ? (
                      <span
                        className="badge-interesse-empresa"
                        title="Este convidado selecionou sua empresa como interesse de conversa"
                      >
                        <FaHandshake aria-hidden />
                        Participante demonstrou interesse na sua empresa
                      </span>
                    ) : null}
                  </div>
                  <div className="acoes-participante">
                    <button
                      onClick={() => adicionarAoGrupo(participante, 'mais')}
                      className="btn-acao btn-mais"
                      disabled={maisQueroConversar.length >= limiteMaisQuero}
                      title="Agendamento Prioritário"
                    >
                      <FaStar />
                    </button>
                    <button
                      onClick={() => adicionarAoGrupo(participante, 'menos')}
                      className="btn-acao btn-menos"
                      disabled={menosQueroConversar.length >= limiteQuero}
                      title="Agendamento Desejável"
                    >
                      <FaThumbsUp />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Grupos Selecionados */}
        <div className="grupos-selecionados">
          {/* Mais quero conversar */}
          <div className="grupo-container mais">
            <h3 className="titulo-grupo">
              <FaStar /> AGENDAMENTO PRIORITÁRIO 
              {/* <span className="instrucao-limites">( Escolha entre {minimoMaisQuero} e {limiteMaisQuero} participantes )</span> */}
              <span className="instrucao-limites">(executivos fundamentais para seus objetivos de negócio)</span>
              <span className="contador-participantes">
                {maisQueroConversar.length}/{limiteMaisQuero}
              </span>
            </h3>
            <div className="lista-grupo">
              {maisQueroConversar.map(participante => (
                <div
                  key={participante.id}
                  className={`item-grupo${participante.interesse_empresa ? " tem-interesse" : ""}`}
                >
                  <div className="info-item-grupo">
                    <span className="nome-item">{participante.nome}</span>
                    <span className="cargo-item">{participante.cargo}</span>
                    <span className="empresa-item">{participante.empresa}</span>
                    {participante.interesse_empresa ? (
                      <span className="badge-interesse-empresa badge-interesse-empresa--compacto">
                        <FaHandshake aria-hidden />
                        Interesse
                      </span>
                    ) : null}
                  </div>
                  <button
                    onClick={() => removerDoGrupo(participante.id, 'mais')}
                    className="btn-remover"
                    title="Remover do grupo"
                  >
                    ×
                  </button>
                </div>
              ))}
              {maisQueroConversar.length === 0 && (
                <div className="grupo-vazio-container">
                  <p className="grupo-vazio">Nenhum participante selecionado</p>
                </div>
              )}
            </div>
          </div>

          {/* Menos quero conversar */}
          <div className="grupo-container menos">
            <h3 className="titulo-grupo">
              <FaThumbsUp /> AGENDAMENTO DESEJÁVEL
              <span className="instrucao-limites">
                (executivos de interesse secundário)
                {/* ( Escolha entre {minimoQuero} e {limiteQuero} participantes ) */}
              </span>
              <span className="contador-participantes">
                {menosQueroConversar.length}/{limiteQuero}
              </span>
            </h3>
            <div className="lista-grupo">
              {menosQueroConversar.map(participante => (
                <div
                  key={participante.id}
                  className={`item-grupo${participante.interesse_empresa ? " tem-interesse" : ""}`}
                >
                  <div className="info-item-grupo">
                    <span className="nome-item">{participante.nome}</span>
                    <span className="cargo-item">{participante.cargo}</span>
                    <span className="empresa-item">{participante.empresa}</span>
                    {participante.interesse_empresa ? (
                      <span className="badge-interesse-empresa badge-interesse-empresa--compacto">
                        <FaHandshake aria-hidden />
                        Interesse
                      </span>
                    ) : null}
                  </div>
                  <button
                    onClick={() => removerDoGrupo(participante.id, 'menos')}
                    className="btn-remover"
                    title="Remover do grupo"
                  >
                    ×
                  </button>
                </div>
              ))}
              {menosQueroConversar.length === 0 && (
                <div className="grupo-vazio-container">
                  <p className="grupo-vazio">Nenhum participante selecionado</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Container Compacto: Status + Botões */}
      <div className="footer-compacto">
        <div className="status-requisitos">
          <div className="requisitos-mini">
            <span className="status-item ok">
              ⭐ {maisQueroConversar.length}/{limiteMaisQuero}
            </span>
            <span className="status-item ok">
              👍 {menosQueroConversar.length}/{limiteQuero}
            </span>
          </div>
          <div className="status-mensagem ok">
            {totalSelecionados === 0
              ? "Salve quando quiser — pode continuar depois enquanto o link estiver válido"
              : "Você pode salvar e continuar editando enquanto o link estiver válido"}
          </div>
        </div>
        
        <div className="botoes-acao">
          <button
            onClick={() => setShowModal(true)}
            disabled={totalSelecionados === 0}
            className="btn-action btn-visualizar"
          >
            <FaEye />
            Visualizar
          </button>
          <button
            onClick={salvarSelecoes}
            disabled={saving}
            className={`btn-action btn-salvar ${saving ? 'salvando' : ''}`}
          >
            <FaSave />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* Modal de Visualização */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Participantes Selecionados</h3>
              <button 
                className="btn-fechar-modal"
                onClick={() => setShowModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              {(maisQueroConversar.length > 0 || menosQueroConversar.length > 0) ? (
                <div className="tabela-container">
                  <table className="tabela-participantes">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Empresa</th>
                        <th>Cargo</th>
                        <th>Grupo</th>
                        <th>Match &amp; Meet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maisQueroConversar.map(participante => (
                        <tr key={`mais-${participante.id}`}>
                          <td>{participante.nome}</td>
                          <td>{participante.empresa}</td>
                          <td>{participante.cargo}</td>
                          <td className="grupo-mais">
                            <span className="badge-grupo mais">
                              <FaStar />
                              <span>Agendamento Prioritário</span>
                            </span>
                          </td>
                          <td>
                            {participante.interesse_empresa ? (
                              <span className="badge-interesse-empresa">
                                <FaHandshake aria-hidden />
                                Interesse
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                      {menosQueroConversar.map(participante => (
                        <tr key={`menos-${participante.id}`}>
                          <td>{participante.nome}</td>
                          <td>{participante.empresa}</td>
                          <td>{participante.cargo}</td>
                          <td className="grupo-menos">
                            <span className="badge-grupo menos">
                              <FaThumbsUp />
                              <span>Agendamento Desejável</span>
                            </span>
                          </td>
                          <td>
                            {participante.interesse_empresa ? (
                              <span className="badge-interesse-empresa">
                                <FaHandshake aria-hidden />
                                Interesse
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="sem-selecoes">
                  <p>Nenhum participante foi selecionado ainda.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelecaoParticipantes; 